"use client";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type WheelEvent as ReactWheelEvent,
} from "react";

import { resolveImageWheelAction } from "@/hooks/resolveImageWheelAction";

interface ImageTransform {
  scale: number;
  x: number;
  y: number;
}

interface PointerPosition {
  x: number;
  y: number;
}

interface DragStart {
  pointer: PointerPosition;
  startedAt: number;
  transform: ImageTransform;
}

interface PinchStart {
  center: PointerPosition;
  distance: number;
  focalPoint: PointerPosition;
  transform: ImageTransform;
}

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const SCALE_STEP = 0.5;
const SWIPE_MIN_DISTANCE = 48;
const SWIPE_MAX_DURATION = 700;
const SWIPE_DIRECTION_RATIO = 1.25;
const INITIAL_TRANSFORM: ImageTransform = { scale: MIN_SCALE, x: 0, y: 0 };

interface ImageZoomOptions {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
}

function calculatePinchTransform(
  pointers: ReadonlyMap<number, PointerPosition>,
  pinchStart: PinchStart | null,
): ImageTransform | null {
  if (pointers.size !== 2 || !pinchStart) return null;
  const [first, second] = Array.from(pointers.values());
  if (!first || !second) return null;

  const center = { x: (first.x + second.x) / 2, y: (first.y + second.y) / 2 };
  const distance = Math.max(Math.hypot(second.x - first.x, second.y - first.y), 1);
  const scale = Math.min(
    Math.max(pinchStart.transform.scale * (distance / pinchStart.distance), MIN_SCALE),
    MAX_SCALE,
  );
  const scaleRatio = scale / pinchStart.transform.scale;

  return {
    scale,
    x:
      pinchStart.focalPoint.x -
      (pinchStart.focalPoint.x - pinchStart.transform.x) * scaleRatio +
      (center.x - pinchStart.center.x),
    y:
      pinchStart.focalPoint.y -
      (pinchStart.focalPoint.y - pinchStart.transform.y) * scaleRatio +
      (center.y - pinchStart.center.y),
  };
}

function shouldTrackSwipe(
  event: ReactPointerEvent<HTMLDivElement>,
  swipeEnabled: boolean,
  pointerCount: number,
  scale: number,
): boolean {
  return swipeEnabled && event.pointerType === "touch" && pointerCount === 1 && scale === MIN_SCALE;
}

function isHorizontalSwipe(deltaX: number, deltaY: number, elapsed: number): boolean {
  return (
    Math.abs(deltaX) >= SWIPE_MIN_DISTANCE &&
    Math.abs(deltaX) > Math.abs(deltaY) * SWIPE_DIRECTION_RATIO &&
    elapsed <= SWIPE_MAX_DURATION
  );
}

function completedSwipeDirection(
  event: ReactPointerEvent<HTMLDivElement>,
  dragStart: DragStart | null,
  pointerCount: number,
  currentScale: number,
): "left" | "right" | null {
  if (event.type !== "pointerup" || event.pointerType !== "touch" || pointerCount !== 1)
    return null;
  if (!dragStart || dragStart.transform.scale !== MIN_SCALE || currentScale !== MIN_SCALE)
    return null;

  const deltaX = event.clientX - dragStart.pointer.x;
  const deltaY = event.clientY - dragStart.pointer.y;
  const elapsed = performance.now() - dragStart.startedAt;
  if (!isHorizontalSwipe(deltaX, deltaY, elapsed)) return null;
  return deltaX < 0 ? "left" : "right";
}

function dragStartFromPointer(
  pointer: PointerPosition | undefined,
  transform: ImageTransform,
): DragStart | null {
  if (!pointer) return null;
  return { pointer, startedAt: performance.now(), transform };
}

export function useImageZoom(
  resetKey: string,
  { onSwipeLeft, onSwipeRight }: ImageZoomOptions = {},
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const transformRef = useRef<ImageTransform>(INITIAL_TRANSFORM);
  const pointerPositionsRef = useRef(new Map<number, PointerPosition>());
  const dragStartRef = useRef<DragStart | null>(null);
  const pinchStartRef = useRef<PinchStart | null>(null);
  // Keep momentum locked across image changes so one trackpad swipe advances only once.
  const wheelNavigationRef = useRef({
    accumulatedX: 0,
    accumulatedY: 0,
    lastEventAt: Number.NEGATIVE_INFINITY,
    navigated: false,
  });
  const [transform, setTransform] = useState<ImageTransform>(INITIAL_TRANSFORM);
  const [swipeOffsetX, setSwipeOffsetX] = useState(0);
  const [isSwipeTracking, setIsSwipeTracking] = useState(false);
  const swipeEnabled = Boolean(onSwipeLeft || onSwipeRight);

  const clampTransform = useCallback((nextTransform: ImageTransform): ImageTransform => {
    const scale = Math.min(Math.max(nextTransform.scale, MIN_SCALE), MAX_SCALE);
    const bounds = containerRef.current?.getBoundingClientRect();

    if (!bounds || scale === MIN_SCALE) return INITIAL_TRANSFORM;

    const maxX = (bounds.width * (scale - MIN_SCALE)) / 2;
    const maxY = (bounds.height * (scale - MIN_SCALE)) / 2;

    return {
      scale,
      x: Math.min(Math.max(nextTransform.x, -maxX), maxX),
      y: Math.min(Math.max(nextTransform.y, -maxY), maxY),
    };
  }, []);

  const applyTransform = useCallback(
    (nextTransform: ImageTransform) => {
      const clampedTransform = clampTransform(nextTransform);
      transformRef.current = clampedTransform;
      setTransform(clampedTransform);
    },
    [clampTransform],
  );

  const zoomTo = useCallback(
    (nextScale: number, clientPoint?: PointerPosition) => {
      const currentTransform = transformRef.current;
      const scale = Math.min(Math.max(nextScale, MIN_SCALE), MAX_SCALE);
      const bounds = containerRef.current?.getBoundingClientRect();

      if (!clientPoint || !bounds || currentTransform.scale === scale) {
        applyTransform({ ...currentTransform, scale });
        return;
      }

      const focalX = clientPoint.x - bounds.left - bounds.width / 2;
      const focalY = clientPoint.y - bounds.top - bounds.height / 2;
      const scaleRatio = scale / currentTransform.scale;

      applyTransform({
        scale,
        x: focalX - (focalX - currentTransform.x) * scaleRatio,
        y: focalY - (focalY - currentTransform.y) * scaleRatio,
      });
    },
    [applyTransform],
  );

  const resetZoom = useCallback(() => {
    applyTransform(INITIAL_TRANSFORM);
  }, [applyTransform]);

  const zoomIn = useCallback(() => {
    zoomTo(transformRef.current.scale + SCALE_STEP);
  }, [zoomTo]);

  const zoomOut = useCallback(() => {
    zoomTo(transformRef.current.scale - SCALE_STEP);
  }, [zoomTo]);

  useEffect(() => {
    resetZoom();
    pointerPositionsRef.current.clear();
    dragStartRef.current = null;
    pinchStartRef.current = null;
    setSwipeOffsetX(0);
    setIsSwipeTracking(false);
  }, [resetKey, resetZoom]);

  useEffect(() => {
    function handleResize() {
      applyTransform(transformRef.current);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [applyTransform]);

  const handlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.setPointerCapture(event.pointerId);

      const position = { x: event.clientX, y: event.clientY };
      pointerPositionsRef.current.set(event.pointerId, position);

      if (pointerPositionsRef.current.size === 1) {
        if (
          swipeEnabled &&
          event.pointerType === "touch" &&
          transformRef.current.scale === MIN_SCALE
        ) {
          setSwipeOffsetX(0);
          setIsSwipeTracking(true);
        }
        dragStartRef.current = {
          pointer: position,
          startedAt: performance.now(),
          transform: transformRef.current,
        };
        return;
      }

      if (pointerPositionsRef.current.size === 2) {
        setSwipeOffsetX(0);
        setIsSwipeTracking(false);
        const [first, second] = Array.from(pointerPositionsRef.current.values());
        const bounds = containerRef.current?.getBoundingClientRect();
        if (!first || !second || !bounds) return;

        const center = {
          x: (first.x + second.x) / 2,
          y: (first.y + second.y) / 2,
        };

        pinchStartRef.current = {
          center,
          distance: Math.max(Math.hypot(second.x - first.x, second.y - first.y), 1),
          focalPoint: {
            x: center.x - bounds.left - bounds.width / 2,
            y: center.y - bounds.top - bounds.height / 2,
          },
          transform: transformRef.current,
        };
        dragStartRef.current = null;
      }
    },
    [swipeEnabled],
  );

  const handlePointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (!pointerPositionsRef.current.has(event.pointerId)) return;

      event.preventDefault();
      pointerPositionsRef.current.set(event.pointerId, {
        x: event.clientX,
        y: event.clientY,
      });

      const pinchTransform = calculatePinchTransform(
        pointerPositionsRef.current,
        pinchStartRef.current,
      );
      if (pinchTransform) {
        applyTransform(pinchTransform);
        return;
      }

      const dragStart = dragStartRef.current;
      if (!dragStart) return;

      if (
        shouldTrackSwipe(
          event,
          swipeEnabled,
          pointerPositionsRef.current.size,
          transformRef.current.scale,
        )
      ) {
        setSwipeOffsetX(event.clientX - dragStart.pointer.x);
        return;
      }

      if (transformRef.current.scale === MIN_SCALE) return;

      applyTransform({
        ...dragStart.transform,
        x: dragStart.transform.x + event.clientX - dragStart.pointer.x,
        y: dragStart.transform.y + event.clientY - dragStart.pointer.y,
      });
    },
    [applyTransform, swipeEnabled],
  );

  const handlePointerEnd = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const dragStart = dragStartRef.current;
      const swipeDirection = completedSwipeDirection(
        event,
        dragStart,
        pointerPositionsRef.current.size,
        transformRef.current.scale,
      );
      if (swipeDirection === "left") onSwipeLeft?.();
      if (swipeDirection === "right") onSwipeRight?.();

      pointerPositionsRef.current.delete(event.pointerId);
      pinchStartRef.current = null;
      setSwipeOffsetX(0);
      setIsSwipeTracking(false);

      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }

      const [remainingPointer] = Array.from(pointerPositionsRef.current.values());
      dragStartRef.current = dragStartFromPointer(remainingPointer, transformRef.current);
    },
    [onSwipeLeft, onSwipeRight],
  );

  const handleWheel = useCallback(
    (event: ReactWheelEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();

      const canNavigate = swipeEnabled && transformRef.current.scale === MIN_SCALE;
      const resolution = resolveImageWheelAction(
        wheelNavigationRef.current,
        {
          deltaX: event.deltaX,
          deltaY: event.deltaY,
          occurredAt: performance.now(),
          shiftKey: event.shiftKey,
        },
        canNavigate,
      );
      wheelNavigationRef.current = resolution.state;

      if (resolution.action === "next") return onSwipeLeft?.();
      if (resolution.action === "previous") return onSwipeRight?.();
      if (resolution.action === null) return;

      const scaleDirection = resolution.action === "zoom-in" ? 1 : -1;
      zoomTo(transformRef.current.scale + scaleDirection * SCALE_STEP, {
        x: event.clientX,
        y: event.clientY,
      });
    },
    [onSwipeLeft, onSwipeRight, swipeEnabled, zoomTo],
  );

  const handleDoubleClick = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      event.stopPropagation();
      zoomTo(transformRef.current.scale > MIN_SCALE ? MIN_SCALE : 2, {
        x: event.clientX,
        y: event.clientY,
      });
    },
    [zoomTo],
  );

  return {
    canZoomIn: transform.scale < MAX_SCALE,
    canZoomOut: transform.scale > MIN_SCALE,
    containerRef,
    handleDoubleClick,
    handlePointerDown,
    handlePointerEnd,
    handlePointerMove,
    handleWheel,
    resetZoom,
    isSwipeTracking,
    swipeOffsetX,
    transform,
    zoomIn,
    zoomOut,
    zoomPercentage: Math.round(transform.scale * 100),
  };
}
